import React from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Button from '@mui/material/Button';
import classNames from 'classnames';
import useDebouncedEffect from '@/lib/hooks/useDebouncedEffect';
import { useCurrentUser } from '@/lib/hooks/userData';
import useStatefulId from '@/lib/hooks/useStatefulId';
import translate from '@/lib/intl/translate';
import type AttachmentItem from '@/types/api/AttachmentItem';
import type { FileUploadItem } from '../fileUploader';
import renameDuplicateFile from '../lib/renameDuplicateFile';
import UploadContexts from '../lib/UploadContexts';
import DropTarget from './DropTarget';
import FileItem from './FileItem';

export type FileUploaderProps = {
  accept?: string;
  children?: React.ReactNode;
  className?: string;
  context?: UploadContexts;
  id?: string;
  multiple?: boolean;
  onChange?: (event: FileUploadItem[]) => void;
  onError?: (event: unknown, item?: FileUploadItem) => void;
  onUploadFinished?: (event: FileUploadItem[]) => void;
  preprocess?: (files: File[]) => Promise<File[]>;
  showDropTarget?: boolean;
  validate?: (event: File[]) => Promise<boolean | string | Error>;
  value?: AttachmentItem[];
};

function DropTargetCTA({ id }: { id: string }) {
  return translate('DRAG_AND_DROP_CTA', {
    span1: (chunks: React.ReactNode) => (
      <span className="inline-flex items-center gap-2 type-body2 hoverless:hidden">
        <CloudUploadIcon className="text-3xl" />
        {chunks}
      </span>
    ),
    span2: (chunks: React.ReactNode) => (
      <span className="hoverless:hidden">{chunks}</span>
    ),
    button: (chunks: React.ReactNode) => (
      <Button
        component="label"
        htmlFor={`${id}-input`}
        variant="contained"
        color="secondary"
      >
        {chunks}
      </Button>
    ),
  });
}

const FileUploader = React.forwardRef<HTMLInputElement, FileUploaderProps>(
  (
    {
      accept = '',
      children = null,
      className = '',
      context = UploadContexts.ATTACHMENTS,
      id = '',
      multiple = false,
      onChange = () => {},
      onError = () => {},
      onUploadFinished = () => {},
      preprocess = (files) => Promise.resolve(files),
      showDropTarget = true,
      validate = () => Promise.resolve(true),
      value = [],
    },
    forwardedRef,
  ) => {
    const { isOrgAdmin, isOrgEditor } = useCurrentUser();
    const statefulId = useStatefulId(id);
    const inputRef = React.useRef<HTMLInputElement>(null);
    React.useImperativeHandle(
      forwardedRef,
      () => inputRef.current as HTMLInputElement,
    );

    const [fileUploadItems, setFileUploadItems] = React.useState(
      value.map(
        ({ fileName, uploadId }: AttachmentItem): FileUploadItem => ({
          file: null,
          fileName,
          id: `${fileName}-${Date.now()}`,
          presignedPost: null,
          uploadId,
        }),
      ),
    );

    const processFiles = async (newFiles: File[]) => {
      try {
        await validate(newFiles);
      } catch (e) {
        onError(e);
        return;
      }

      const files = await preprocess(newFiles);

      setFileUploadItems((oldState) =>
        multiple
          ? files.reduce((arr, newFile) => {
              let addedFile = newFile;
              const nameExists = () =>
                arr.some(
                  (existingItem) => existingItem.fileName === addedFile.name,
                );
              while (nameExists()) {
                addedFile = renameDuplicateFile(addedFile);
              }
              return [
                ...arr,
                {
                  file: addedFile,
                  fileName: addedFile.name,
                  id: `${addedFile.name}-${Date.now()}`,
                  presignedPost: null,
                  uploadId: null,
                },
              ];
            }, oldState)
          : [
              {
                file: files[0],
                fileName: files[0].name,
                id: `${files[0].name}-${Date.now()}`,
                presignedPost: null,
                uploadId: null,
              },
            ],
      );
    };
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newFiles = [...(e.target.files || [])];
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      processFiles(newFiles);
    };

    const handleRemove = (fileName: string) => () =>
      setFileUploadItems((oldFiles) =>
        oldFiles.filter((item) => item.fileName !== fileName),
      );

    const handleFileUploaded =
      (fileName: string) => (newItem: FileUploadItem) => {
        setFileUploadItems((oldFiles) =>
          oldFiles.map((item) => (item.fileName === fileName ? newItem : item)),
        );
      };

    const handleError = (fileName: string) => (e: unknown) => {
      const fileUploadItem = fileUploadItems.find(
        (item) => item.fileName === fileName,
      );
      handleRemove(fileName)();
      return onError(e, fileUploadItem);
    };

    const hasInitiallyRendered = React.useRef(false);
    useDebouncedEffect(
      () => {
        if (!hasInitiallyRendered.current) {
          hasInitiallyRendered.current = true;
        } else {
          onChange(fileUploadItems);

          if (fileUploadItems.every((item) => !!item.uploadId)) {
            onUploadFinished(fileUploadItems);
          }
        }
      },
      [fileUploadItems],
      1000,
    );

    return (
      <div className={className} id={statefulId}>
        {multiple && !!fileUploadItems.length && (
          <ul className={classNames({ 'mb-6': showDropTarget })}>
            {fileUploadItems.map(({ file, fileName, id: fileId, uploadId }) => (
              <FileItem
                context={context}
                file={file}
                fileName={fileName}
                id={fileId}
                key={fileId}
                onError={handleError(fileName)}
                onRemove={handleRemove(fileName)}
                onUpload={handleFileUploaded(fileName)}
                multiple
                showDelete={isOrgAdmin || isOrgEditor}
                uploadId={uploadId}
              />
            ))}
          </ul>
        )}
        {(isOrgAdmin || isOrgEditor) && showDropTarget && (
          <DropTarget onDrop={processFiles}>
            {!multiple && !!fileUploadItems[0] ? (
              <FileItem
                context={context}
                file={fileUploadItems[0].file}
                fileName={fileUploadItems[0].fileName}
                id={fileUploadItems[0].id}
                onError={handleError(fileUploadItems[0].fileName)}
                onRemove={handleRemove(fileUploadItems[0].fileName)}
                onUpload={handleFileUploaded(fileUploadItems[0].fileName)}
                showDelete={isOrgAdmin || isOrgEditor}
                uploadId={fileUploadItems[0].uploadId}
              />
            ) : (
              <>
                <DropTargetCTA id={statefulId} />
                {children}
                <input
                  className="sr-only"
                  type="file"
                  onChange={handleInputChange}
                  ref={inputRef}
                  id={`${statefulId}-input`}
                  accept={accept}
                  multiple={multiple}
                />
              </>
            )}
          </DropTarget>
        )}
      </div>
    );
  },
);

export default FileUploader;
